import gql from 'graphql-tag';

/**
 * Base Fragments
 */

const RoleFields = gql`
  fragment RoleFields on Role {
    id
    name
    label
    type
  }
`;

const RoleIdentifierFields = gql`
  fragment RoleIdentifierFields on RoleIdentifier {
    id
    key
    type
    label
  }
`;

export const MomentTemplateFields = gql`
  fragment MomentTemplateFields on MomentTemplate {
    __typename
    ... on SalvationMoment {
      id
      type
      includedInFutureServices
      updatedAt
      options {
        salvationMomentFollowUpType
        url
        labels {
          prompt
          response
          buttonText
          celebrationMessage
        }
      }
    }
    ... on QuoteMoment {
      id
      type
      includedInFutureServices
      updatedAt
      options {
        text
        attribution
      }
    }
    ... on GivingMoment {
      id
      type
      includedInFutureServices
      updatedAt
      options {
        text
        src
        windowType
      }
    }
    ... on ConnectMoment {
      id
      type
      includedInFutureServices
      updatedAt
      options {
        text
        windowType
        connectUrl: url
      }
    }
    ... on InviteMoment {
      id
      type
      includedInFutureServices
      updatedAt
      options {
        text
        buttonText
      }
    }
    ... on PrayerMoment {
      id
      type
      includedInFutureServices
      updatedAt
      options {
        text
        buttonText
      }
    }
    ... on ResponseMoment {
      id
      type
      includedInFutureServices
      updatedAt
      options {
        text
        buttonText
        symbol {
          id
          name
          native
          unified
        }
      }
    }
    ... on ImageMoment {
      id
      type
      includedInFutureServices
      updatedAt
      options {
        altText
        imageUrl
        shareText
      }
    }
    ... on NextStepsMoment {
      id
      type
      includedInFutureServices
      updatedAt
      options {
        buttonText
        description
        iconUrl
        leadInText
        text
        url
      }
    }
  }
`;

export const MetricTemplateFields = gql`
  fragment MetricTemplateFields on ServiceMetric {
    __typename
    serviceId
    salvations
    msgsPublicByHosts
    msgsPublic
    msgsPrayer
    msgsHost
    msgsDirect
    chattersHosts
    chattersAttenders
    totalTimeWatchedSecs
    avgTimeWatched
    viewers0s
    viewers3s
    viewers10s
    viewers30s
    viewers60s
    viewers5m
    viewers10m
    viewers15m
    viewers20m
    viewers25m
    viewers30m
    hostsPresent
    timelineSubsPresentT
    timelineSubsPresentC
    timelinePrayerT
    timelinePrayerC
  }
`;

const CurrentSubscriberFields = gql`
  fragment CurrentSubscriberFields on Subscriber {
    id
    nickname
    avatar
    firstName
    lastName
    email
    emailConsent
    roleIdentifier {
      id
      type
      key
      label
    }
    role {
      id
      type
      name
      permissions {
        key
      }
    }
  }
`;

export const CURRENT_SUBSCRIBER = gql`
  query CurrentSubscriber {
    currentSubscriber {
      ...CurrentSubscriberFields
    }
  }
  ${CurrentSubscriberFields}
`;

const SubscriberFields = gql`
  fragment SubscriberFields on Subscriber {
    id
    nickname
    avatar
    firstName
    lastName
    email
    muted
    mutedUntil
    lastActivity
    emailConsent
    createdAt
    inviteExpiresAt
    invitePending
    roleIdentifier {
      ...RoleIdentifierFields
    }
    role {
      ...RoleFields
    }
  }
  ${RoleFields}
  ${RoleIdentifierFields}
`;

const SubscriberPageInfoFields = gql`
  fragment SubscriberPageInfoFields on PageInfo {
    endCursor
    hasNextPage
    hasPreviousPage
    startCursor
  }
`;

const SubscriberNodeFields = gql`
  fragment SubscriberNodeFields on SubscriberEdge {
    node {
      ...SubscriberFields
    }
  }
  ${SubscriberFields}
`;

export const SUBSCRIBER_QUERY = gql`
  query Subscriber($id: ID!) {
    subscriber(id: $id) {
      ...SubscriberFields
    }
  }
  ${SubscriberFields}
`;

export const SAVE_SUBSCRIBER = gql`
  mutation SaveSubscriber($id: ID!, $input: SubscriberInput!) {
    saveSubscriber(id: $id, input: $input) {
      subscriber {
        id
      }
      errors {
        code
        message
        property
      }
    }
  }
`;

export const UNMUTE_SUBSCRIBER = gql`
  mutation UnmuteSubscriber($subscriberId: ID!) {
    unmuteSubscriber(subscriberId: $subscriberId) {
      success
      errors {
        code
        message
      }
    }
  }
`;

export const DELETE_SUBSCRIBER = gql`
  mutation DeleteSubscriber($id: ID!) {
    deleteSubscriber(id: $id) {
      subscriber {
        id
      }
      errors {
        code
        message
        property
      }
    }
  }
`;

export const INVITE_USER = gql`
  mutation InviteUser($input: InviteUserInput!) {
    inviteUser(input: $input) {
      subscriber {
        email
        id
        invitePending
        inviteExpiresAt
      }
      errors {
        code
        message
        property
      }
    }
  }
`;

export const REINVITE_USER = gql`
  mutation ReinviteUser($input: ReinviteUserInput!) {
    reinviteUser(input: $input) {
      subscriber {
        id
        invitePending
        inviteExpiresAt
      }
      errors {
        code
        message
        property
      }
    }
  }
`;

export const REQUEST_PASSWORD_RESET_SUBSCRIBER = gql`
  mutation RequestPasswordReset($email: String!) {
    requestPasswordReset(email: $email) {
      success
      errors {
        code
        message
      }
    }
  }
`;

const ApperanceFields = gql`
  fragment ApperanceFields on Appearance {
    favicon
    headerBackgroundColor
    headerTextColor
    navigationLinkCase
    hideViewershipMetric
    logo
    visualMode
    darkLogo
    enableDarkMode
  }
`;

const LinkFields = gql`
  fragment LinkFields on OrganizationLink {
    id
    key
    label
    url
  }
`;

const OrganizationFields = gql`
  fragment OrganizationFields on Organization {
    id
    name
    website
    timezone
    address
    addressTwo
    city
    state
    zip
    country
    slug
    emailConsent
    privacyUrl
    termsUrl
    customDomain
    slugDomain
    roles {
      ...RoleFields
    }
    appearance {
      ...ApperanceFields
    }
    roleIdentifiers {
      ...RoleIdentifierFields
    }
    links {
      ...LinkFields
    }
    loginBanner {
      bodyText
      buttonText
      enabled
    }
    welcomeBanner {
      bodyText
      enabled
      titleText
    }
  }
  ${ApperanceFields}
  ${LinkFields}
  ${RoleFields}
  ${RoleIdentifierFields}
`;

export const CURRENT_ORGANIZATION = gql`
  query CurrentOrganization {
    currentOrganization {
      ...OrganizationFields
    }
  }
  ${OrganizationFields}
`;

export const OrganizationReportingFields = gql`
  fragment OrganizationReportingFields on Organization {
    id
    transcripts {
      scheduleTime
      title
      livePrayers
      directChats
      expires
      url
    }
    reportingConfig {
      id
      transcriptsEnabled
      serviceReportRecipients
    }
  }
`;

export const CURRENT_ORGANIZATION_REPORTING = gql`
  query CurrentOrganizationReporting {
    currentOrganization {
      ...OrganizationReportingFields
    }
  }
  ${OrganizationReportingFields}
`;

const CurrentOrganizationInfoFields = gql`
  fragment CurrentOrganizationInfoFields on Organization {
    id
    name
    website
    timezone
    address
    addressTwo
    city
    state
    zip
    country
    emailConsent
    privacyUrl
    termsUrl
  }
`;

export const CURRENT_ORGANIZATION_INFO = gql`
  query CurrentOrganizationInfo {
    currentOrganization {
      ...CurrentOrganizationInfoFields
    }
  }
  ${CurrentOrganizationInfoFields}
`;

export const OrganizationOfflineFields = gql`
  fragment OrganizationOfflineFields on Organization {
    id
    offlinePrayerRecipients
    offlineContent {
      __typename
      type
      ... on OfflineImage {
        url
      }
      ... on OfflineVideo {
        source
        url
        options {
          autoplay
          loop
        }
      }
    }
  }
`;

export const CURRENT_ORGANIZATION_OFFLINE = gql`
  query CurrentOrganizationOffline {
    currentOrganization {
      ...OrganizationOfflineFields
    }
  }
  ${OrganizationOfflineFields}
`;

export const CURRENT_ORGANIZATION_DOMAIN = gql`
  query CurrentOrganizationDomain {
    currentOrganization {
      id
      customDomain
      slug
    }
  }
`;

export const CURRENT_ORGANIZATION_APPEARANCE = gql`
  query CurrentOrganizationAppearance {
    links {
      id
      key
      label
    }
    currentOrganization {
      id
      name
      appearance {
        ...ApperanceFields
      }
      links {
        ...LinkFields
      }
      loginBanner {
        bodyText
        buttonText
        enabled
      }
      welcomeBanner {
        bodyText
        enabled
        titleText
      }
      versions {
        id
        changes
        createdAt
        event
        whodunnit
        itemType
        subscriberId
        subscriber {
          id
          email
          firstName
          lastName
        }
      }
    }
  }
  ${ApperanceFields}
  ${LinkFields}
`;

const SaveOrganizationFields = gql`
  fragment SaveOrganizationFields on Organization {
    id
    name
    website
    customDomain
    timezone
    address
    addressTwo
    city
    state
    zip
    country
    slug
    emailConsent
    privacyUrl
    termsUrl
    appearance {
      ...ApperanceFields
    }
    links {
      ...LinkFields
    }
    loginBanner {
      bodyText
      buttonText
      enabled
    }
    welcomeBanner {
      bodyText
      enabled
      titleText
    }
    offlineContent {
      type
      ... on OfflineImage {
        url
      }
      ... on OfflineVideo {
        source
        url
        options {
          autoplay
          loop
        }
      }
    }
    offlinePrayerRecipients
    offlineVideoUrl
    shareImage
  }
  ${ApperanceFields}
  ${LinkFields}
`;

export const SAVE_ORGANIZATION = gql`
  mutation SaveOrganization($input: OrganizationInput!) {
    saveOrganization(input: $input) {
      organization {
        ...SaveOrganizationFields
      }
      errors {
        code
        message
        property
      }
    }
  }
  ${SaveOrganizationFields}
`;

export const SAVE_ROLE_IDENTIFIER = gql`
  mutation SaveRoleIdentifier($input: RoleIdentifierInput!) {
    saveRoleIdentifier(input: $input) {
      roleIdentifier {
        id
      }
      errors {
        code
        message
        property
      }
    }
  }
`;

export const SAVE_REPORTING_CONFIG = gql`
  mutation SaveReportingConfig($input: ReportingConfigInput!) {
    saveReportingConfig(input: $input) {
      reportingConfig {
        id
        organizationId
        transcriptsEnabled
        serviceReportRecipients
      }
      errors {
        code
      }
    }
  }
`;

export const SAVE_SERVICE = gql`
  mutation SaveService($id: ID!, $input: ServiceInput!, $applyTo: ApplyEnum!) {
    saveService(id: $id, input: $input, applyTo: $applyTo) {
      services {
        id
      }
      errors {
        code
        message
        property
      }
    }
  }
`;

export const MomentScheduleItemFields = gql`
  fragment MomentScheduleItemFields on MomentScheduleItem {
    id
    startTimeOffset
    ordinal
    momentTemplate {
      ...MomentTemplateFields
    }
  }
  ${MomentTemplateFields}
`;

export const LoadServiceFields = gql`
  fragment LoadServiceFields on Service {
    id
    scheduleTime
    weekOfMonth
    dayOfWeek
    dayOfMonth
    monthOfYear
    recurrence
    endRepeat
    endTime
    versions {
      id
      changes
      createdAt
      event
      whodunnit
      itemType
      subscriber {
        id
        email
        firstName
        lastName
      }
    }
    content {
      id
      title
      duration
      features {
        publicChat
        livePrayer
      }
      chatStartOffset
      chatEndOffset
      video {
        id
        type
        source
      }
      videoStartOffset
      notes
      hostInfo
      momentScheduleItems {
        ...MomentScheduleItemFields
      }
      versions {
        id
        changes
        createdAt
        event
        whodunnit
        itemType
        subscriber {
          id
          email
          firstName
          lastName
        }
      }
    }
  }
  ${MomentScheduleItemFields}
`;

export const LOAD_SERVICE = gql`
  query LoadService($id: ID!) {
    loadService(id: $id) {
      ...LoadServiceFields
    }
  }
  ${LoadServiceFields}
`;

const MomentInstanceMetricFields = gql`
  fragment MomentInstanceMetricFields on MomentInstanceMetric {
    momentInstanceId
    impressions
    interactions
    submissions
    likes
    shares
  }
`;

const MomentInstanceFields = gql`
  fragment MomentInstanceFields on MomentInstance {
    id
    postTime
    deleted
    momentTemplate {
      ...MomentTemplateFields
    }
    metrics {
      ...MomentInstanceMetricFields
    }
  }
  ${MomentTemplateFields}
  ${MomentInstanceMetricFields}
`;

export const ServiceMetricFields = gql`
  fragment ServiceMetricFields on Service {
    id
    startTime
    scheduleTime
    endTime
    content {
      id
      title
    }
    metrics {
      ...MetricTemplateFields
    }
    momentInstances {
      ...MomentInstanceFields
    }
  }
  ${MetricTemplateFields}
  ${MomentInstanceFields}
`;

export const LOAD_SERVICE_METRICS = gql`
  query LoadServiceMetrics($id: ID!) {
    loadService(id: $id) {
      ...ServiceMetricFields
    }
  }
  ${ServiceMetricFields}
`;

export const DELETE_SERVICE = gql`
  mutation DeleteService($id: ID!, $applyTo: ApplyEnum!) {
    deleteService(id: $id, applyTo: $applyTo) {
      service {
        id
      }
      errors {
        code
        message
        property
      }
    }
  }
`;

export const ServiceFields = gql`
  fragment ServiceFields on Service {
    id
    scheduleTime
    startTime
    endTime
    recurrence
    endRepeat
    content {
      id
      title
      video {
        id
        source
      }
    }
  }
`;

export const ServicesConnectionOrganizationFields = gql`
  fragment ServicesConnectionOrganizationFields on Organization {
    servicesConnection(
      from: $from
      to: $to
      limit: $limit
      order: $order
      expandToContentId: $expandToContentId
      filter: $filter
    ) {
      aggregate {
        count
        from
        to
      }
      services {
        ...ServiceFields
      }
    }
  }
  ${ServiceFields}
`;

export const SERVICES_CONNECTION = gql`
  query AdminServicesConnection(
    $from: Timestamp
    $to: Timestamp
    $limit: Int
    $expandToContentId: ID
    $order: OrderType
    $filter: ServicesConnectionFilter
  ) {
    currentOrganization {
      ...ServicesConnectionOrganizationFields
    }
  }
  ${ServicesConnectionOrganizationFields}
`;

const ActiveMetricFields = gql`
  fragment ActiveMetricFields on AnalyticsOverviewMetric {
    accountsCreated
    avgPrayerMessages
    avgTimeAttended
    offlineRequests
    prayerRequesters
    prayerRequests
    prayerSessions
    salvations
    timepoint
    totalTimeAttended
    uniqueAttenders
    uniqueAttendersPreviousYear
    uniqueHosts
    __typename
  }
`;

const ComparisonMetricFields = gql`
  fragment ComparisonMetricFields on AnalyticsOverviewMetric {
    accountsCreated
    avgPrayerMessages
    offlineRequests
    prayerRequesters
    prayerRequests
    prayerSessions
    timepoint
    uniqueAttenders
    uniqueHosts
    __typename
  }
`;

const AnalyticsOverviewFields = gql`
  fragment AnalyticsOverviewFields on AnalyticsOverviewMetrics {
    activeMetrics {
      ...ActiveMetricFields
    }
    comparisonMetrics {
      ...ComparisonMetricFields
    }
    groupingUnit
  }
`;

const ServicesConnectionFields = gql`
  fragment ServicesConnectionFields on ServicesConnection {
    aggregate {
      count
      from
      to
    }
    services {
      id
      scheduleTime
      startTime
      endTime
      recurrence
      metrics {
        serviceId
        viewers0s
      }
    }
  }
`;

export const AdminOverviewConnectionOrganizationFields = gql`
  fragment AdminOverviewConnectionOrganizationFields on Organization {
    analyticsOverview(from: $from, to: $to) {
      ...AnalyticsOverviewFields
    }
    servicesConnection(
      from: $from
      to: $to
      limit: $limit
      expandToContentId: $expandToContentId
      filter: $filter
    ) {
      ...ServicesConnectionFields
    }
  }
  ${AnalyticsOverviewFields}
  ${ActiveMetricFields}
  ${ComparisonMetricFields}
  ${ServicesConnectionFields}
`;

export const OVERVIEW_CONNECTION = gql`
  query AdminOverviewConnection(
    $from: Timestamp
    $to: Timestamp
    $limit: Int
    $expandToContentId: ID
    $filter: ServicesConnectionFilter
  ) {
    currentOrganization {
      ...AdminOverviewConnectionOrganizationFields
    }
  }
  ${AdminOverviewConnectionOrganizationFields}
`;

export const SAVE_CONTENT = gql`
  mutation SaveContent($id: ID!, $input: ContentInput!) {
    saveContent(id: $id, input: $input) {
      content {
        id
      }
      errors {
        code
        message
        property
      }
    }
  }
`;

export const REMOVE_CONTENT = gql`
  mutation RemoveContent($id: ID!, $services: [ServiceInput!]!) {
    removeContent(id: $id, services: $services) {
      content {
        id
      }
      errors {
        code
        message
        property
      }
    }
  }
`;

export const SUBSCRIBERS = gql`
  query Subscribers(
    $first: Int
    $last: Int
    $before: String
    $after: String
    $order: [SubscriberSortOrder!]
    $filter: SubscribersFilter
  ) {
    currentOrganization {
      id
      subscribers(
        first: $first
        last: $last
        after: $after
        before: $before
        filter: $filter
        order: $order
      ) {
        edges {
          ...SubscriberNodeFields
        }
        pageInfo {
          ...SubscriberPageInfoFields
        }
        totalCount
      }
    }
  }
  ${SubscriberNodeFields}
  ${SubscriberPageInfoFields}
`;

export const SAVE_MOMENT_SCHEDULE = gql`
  mutation SaveMomentSchedule($contentId: ID!, $input: [MomentScheduleItemInput!]!) {
    saveMomentSchedule(contentId: $contentId, input: $input) {
      errors {
        code
        property
      }
    }
  }
`;

export const MOMENT_TEMPLATES_QUERY = gql`
  query MomentTemplates {
    currentOrganization {
      momentTemplates {
        ...MomentTemplateFields
      }
    }
  }
  ${MomentTemplateFields}
`;

export const MOMENT_TEMPLATE_QUERY = gql`
  query MomentTemplateQuery($id: ID!) {
    momentTemplate(id: $id) {
      ...MomentTemplateFields
    }
  }
  ${MomentTemplateFields}
`;

export const NEXT_STEPS_TEMPLATES_QUERY = gql`
  query NextStepsTemplates {
    currentOrganization {
      nextStepsTemplates {
        ...MomentTemplateFields
      }
    }
  }
  ${MomentTemplateFields}
`;

export const EXPORT_USERS = gql`
  mutation ExportUsers {
    exportUsers {
      downloadUrl
    }
  }
`;

export const LOAD_LAST_VIDEO_EMBED = gql`
  query LoadLastVideoEmbed {
    loadLastVideoEmbed
  }
`;

export const SETUP_SHARING = gql`
  query SetupSharing {
    currentOrganization {
      id
      shareImage
      shareDescription
    }
  }
`;

const OnboardingStepFields = gql`
  fragment OnboardingStepFields on OnboardingStep {
    id
    key
    ordinal
    skipped
    completed
  }
`;

export const CURRENT_ORGANIZATION_ONBOARDING = gql`
  query CurrentOrganizationOnboarding {
    currentOrganization {
      id
      name
      onboardingSteps {
        ...OnboardingStepFields
      }
    }
  }
  ${OnboardingStepFields}
`;

export const SAVE_ONBOARDING_STEP = gql`
  mutation SaveOnboardingStep($input: OnboardingStepInput!) {
    saveOnboardingStep(input: $input) {
      onboardingStep {
        ...OnboardingStepFields
      }
      errors {
        code
        message
        property
      }
    }
  }
  ${OnboardingStepFields}
`;

export const SAVE_PREVIEW_SERVICE = gql`
  mutation SavePreviewService($id: ID!) {
    savePreviewService(id: $id) {
      job {
        id
        updateTime
        at
        total
        message
        percentComplete
        status
        meta {
          serviceId
        }
      }
      errors {
        code
        message
      }
    }
  }
`;

export const EVENT_JOB = gql`
  query EventJob($jobId: ID!) {
    eventJob(jobId: $jobId) {
      id
      at
      message
      percentComplete
      status
      total
      updateTime
      ... on PreviewServiceJob {
        meta {
          serviceId
        }
      }
    }
  }
`;
